import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Users, Send, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getPushStats, sendTestPush } from '@/lib/api';

const AdminNotifications = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadStats = async () => {
    try {
      const response = await getPushStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleTestPush = async () => {
    setSending(true);
    try {
      const response = await sendTestPush();
      toast.success(`Notifications envoyées : ${response.data.sent} succès, ${response.data.failed} échecs`);
      loadStats(); // Refresh stats
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des notifications de test');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Notifications Push">
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Notifications Push">
      <div className="space-y-6">
        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900 mb-1">À propos des notifications push</h3>
                <p className="text-sm text-blue-800">
                  Les notifications push permettent d'alerter les invités directement sur leur appareil lorsque :
                </p>
                <ul className="text-sm text-blue-800 mt-2 list-disc list-inside space-y-1">
                  <li>Un nouvel article est publié sur le blog</li>
                  <li>Leur demande d'hébergement est confirmée</li>
                  <li>Une mise à jour importante est envoyée</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-stone flex items-center gap-2">
                <Users className="w-4 h-4" />
                Total abonnés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-display text-charcoal">
                {stats?.total_subscriptions || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-stone flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Abonnés actifs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-display text-green-600">
                {stats?.active_subscriptions || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-stone flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                Inactifs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-display text-red-500">
                {stats?.inactive_subscriptions || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-terracotta" />
              Actions
            </CardTitle>
            <CardDescription>
              Testez le système de notifications push
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={handleTestPush}
                disabled={sending || stats?.active_subscriptions === 0}
                className="bg-terracotta hover:bg-terracotta/90"
                data-testid="send-test-push-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4 mr-2" />
                    Envoyer une notification de test
                  </>
                )}
              </Button>

              {stats?.active_subscriptions === 0 && (
                <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                  Aucun abonné actif
                </Badge>
              )}
            </div>

            <p className="text-xs text-stone mt-4">
              Note : Les notifications sont envoyées automatiquement lors de la publication d'un article 
              ou de la confirmation d'un hébergement.
            </p>
          </CardContent>
        </Card>

        {/* How it works */}
        <Card>
          <CardHeader>
            <CardTitle>Comment ça fonctionne ?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0">
                <span className="text-terracotta font-bold">1</span>
              </div>
              <div>
                <h4 className="font-medium text-charcoal">L'invité visite le site</h4>
                <p className="text-sm text-stone">Après quelques secondes, une petite fenêtre lui propose de s'abonner aux notifications.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0">
                <span className="text-terracotta font-bold">2</span>
              </div>
              <div>
                <h4 className="font-medium text-charcoal">Il accepte les notifications</h4>
                <p className="text-sm text-stone">Son navigateur lui demande confirmation, puis il est abonné.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0">
                <span className="text-terracotta font-bold">3</span>
              </div>
              <div>
                <h4 className="font-medium text-charcoal">Il reçoit les actualités</h4>
                <p className="text-sm text-stone">Dès qu'un article est publié ou qu'une mise à jour importante est disponible, il reçoit une notification sur son appareil.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminNotifications;
